import type {
  NodeDefinition,
  PropertySortV3,
  QueryRequest,
  QueryResponse,
  QuerySelectV3,
  QueryTableExpressionV3,
  TableExpressionFilterDefinition,
  ViewDefinition,
  ViewReference,
} from '@cognite/sdk';

import {
  getNodeProperties,
  getPropertySourceIfDefined,
  getPropertyTypeIfDefined,
} from './utils';

export interface DirectRelationProperty {
  propertyKey: string;
  viewReference: ViewReference;
}

export interface QueryBuilderResult {
  query: QueryRequest;
  baseNodeKey: string;
  directRelationProperties: DirectRelationProperty[];
  relationKeys: { relationKey: string; propertyKey: string }[];
}

export const getDirectRelationProperties = (
  viewDefinition: ViewDefinition | undefined
): DirectRelationProperty[] => {
  if (!viewDefinition) {
    return [];
  }

  const directRelations: DirectRelationProperty[] = [];

  Object.entries(viewDefinition.properties).forEach(([key, property]) => {
    const propertyType = getPropertyTypeIfDefined(property);
    const propertySource = getPropertySourceIfDefined(property);

    if (propertyType === 'direct' && propertySource) {
      directRelations.push({
        propertyKey: key,
        viewReference: propertySource,
      });
    }
  });

  return directRelations;
};

export const buildWithClauses = (
  baseNodeKey: string,
  viewReference: ViewReference,
  directRelationProperties: DirectRelationProperty[],
  sort?: PropertySortV3[],
  filter?: TableExpressionFilterDefinition,
  limit?: number
): Record<string, QueryTableExpressionV3> => {
  const filterClauses: TableExpressionFilterDefinition[] = [];

  if (filter) {
    filterClauses.push(filter);
  }

  filterClauses.push({
    hasData: [viewReference],
  });

  const withClauses: Record<string, QueryTableExpressionV3> = {
    [baseNodeKey]: {
      limit,
      ...(sort && { sort }),
      nodes: {
        filter: {
          and: filterClauses,
        },
      },
    },
  };

  directRelationProperties.forEach((directRelation, index) => {
    const relationKey = `${baseNodeKey}_${directRelation.propertyKey}_${index}`;
    withClauses[relationKey] = {
      limit: 20,
      nodes: {
        from: baseNodeKey,
        through: {
          view: viewReference,
          identifier: directRelation.propertyKey,
        },
        direction: 'outwards',
      },
    };
  });

  return withClauses;
};

export const buildSelectClauses = (
  baseNodeKey: string,
  viewReference: ViewReference,
  directRelationProperties: DirectRelationProperty[]
): Record<string, QuerySelectV3> => {
  const selectClauses: Record<string, QuerySelectV3> = {
    [baseNodeKey]: {
      sources: [
        {
          source: viewReference,
          properties: ['*'],
        },
      ],
    },
  };

  directRelationProperties.forEach((directRelation, index) => {
    const relationKey = `${baseNodeKey}_${directRelation.propertyKey}_${index}`;
    selectClauses[relationKey] = {
      sources: [
        {
          source: directRelation.viewReference,
          properties: ['name', 'description'],
        },
      ],
    };
  });

  return selectClauses;
};

/**
 * Builds DM query for fetching nodes with specified properties for direct relation
 * in a single efficient request.
 *
 * @param viewReference - The view reference for the base node
 * @param viewDefinition - The view definition of the base node to inspect for direct relations
 * @param sort - Optional sort
 * @param filter - Optional filter
 * @returns Query builder result containing the query and metadata
 *
 * Documentation: https://api-docs.cognite.com/20230101/tag/Instances/operation/queryContent
 */
export function buildQuery(
  viewReference: ViewReference,
  viewDefinition: ViewDefinition | undefined,
  sort?: PropertySortV3[],
  filter?: TableExpressionFilterDefinition,
  limit?: number,
  cursor?: string
): QueryBuilderResult {
  const baseNodeKey = `${viewReference.externalId}/${viewReference.version}`;
  /**
   * TODO: (FO-2465)
   * There is a limit on how many additional views can be specified per query - 10.
   * As of now FieldObservation won't hit that limit (we only have 3 direct relation props)
   * But this should be addressed before we allow to extend data model
   */
  const directRelationProperties = getDirectRelationProperties(viewDefinition);

  const query: QueryRequest = {
    with: buildWithClauses(
      baseNodeKey,
      viewReference,
      directRelationProperties,
      sort,
      filter,
      limit
    ),
    select: buildSelectClauses(
      baseNodeKey,
      viewReference,
      directRelationProperties
    ),
  };

  if (cursor) {
    query.cursors = { [baseNodeKey]: cursor };
  }

  const relationKeys = directRelationProperties.map(
    (directRelation, index) => ({
      relationKey: `${baseNodeKey}_${directRelation.propertyKey}_${index}`,
      propertyKey: directRelation.propertyKey,
    })
  );

  return {
    query,
    baseNodeKey,
    directRelationProperties,
    relationKeys,
  };
}

/**
 * Build a map of direct relations by their property key for easy lookup
 * Structure: { propertyKey -> { externalId -> relatedNode } }
 */
export const buildDirectRelationMap = (
  queryResults: QueryResponse,
  relationKeys: { relationKey: string; propertyKey: string }[]
): Record<string, Record<string, NodeDefinition>> => {
  const directRelationMap: Record<string, Record<string, NodeDefinition>> = {};

  relationKeys.forEach(({ relationKey, propertyKey }) => {
    const relatedNodes = queryResults.items[relationKey] || [];
    if (!directRelationMap[propertyKey]) {
      directRelationMap[propertyKey] = {};
    }

    relatedNodes.forEach((item) => {
      if (item.instanceType === 'node') {
        directRelationMap[propertyKey][item.externalId] = item;
      }
    });
  });

  return directRelationMap;
};

export const enrichNodesWithRelations = (
  nodes: NodeDefinition[],
  directRelationMap: Record<string, Record<string, NodeDefinition>>,
  directRelationProperties: DirectRelationProperty[],
  viewReference: ViewReference,
  baseNodeKey: string
): NodeDefinition[] => {
  return nodes.map((node) => {
    const enrichedNode = { ...node };
    const nodeProperties =
      enrichedNode.properties?.[viewReference.space]?.[baseNodeKey];

    if (nodeProperties) {
      directRelationProperties.forEach(({ propertyKey, viewReference }) => {
        const propertyValue = nodeProperties[propertyKey];
        if (Array.isArray(propertyValue)) {
          nodeProperties[propertyKey] = propertyValue.map((ref) => {
            const relatedNode =
              directRelationMap[propertyKey]?.[ref.externalId];
            if (relatedNode) {
              return {
                ...ref,
                ...getNodeProperties(relatedNode, viewReference),
              };
            }
            return ref;
          });
        } else if (
          typeof propertyValue === 'object' &&
          propertyValue !== null &&
          'space' in propertyValue &&
          'externalId' in propertyValue
        ) {
          const relatedNode =
            directRelationMap[propertyKey]?.[
              propertyValue.externalId as string
            ];
          if (relatedNode) {
            nodeProperties[propertyKey] = {
              ...propertyValue,
              ...getNodeProperties(relatedNode, viewReference),
            };
          }
        }
      });
    }

    return enrichedNode;
  });
};
